import dayjs from 'dayjs';

export enum FluxoCaixaTipo {
  ENTRADA = 'entrada',
  SAIDA = 'saida',
}

export enum FluxoCaixaCategoria {
  VENDA = 'venda',
  COMISSAO = 'comissao',
  DEVOLVIDA = 'devolvida',
  MULTA_CLIENTE = 'multa_cliente',
  JUROS = 'juros',
  OUTRO_ENTRADA = 'outro_entrada',
  SALARIO = 'salario',
  COMISSAO_PAGA = 'comissao_paga',
  ALUGUEL = 'aluguel',
  ENERGIA = 'energia',
  AGUA = 'agua',
  INTERNET = 'internet',
  TELEFONE = 'telefone',
  MANUTENCAO = 'manutencao',
  IMPOSTO = 'imposto',
  FORNECEDOR = 'fornecedor',
  TRANSPORTE = 'transporte',
  MARKETING = 'marketing',
  OUTRO_SAIDA = 'outro_saida',
}

export enum FluxoCaixaCentroCusto {
  OPERACIONAL = 'operacional',
  COMERCIAL = 'comercial',
  ADMINISTRATIVO = 'administrativo',
  FINANCEIRO = 'financeiro',
  RECURSOS_HUMANOS = 'recursos_humanos',
  TECNOLOGIA = 'tecnologia',
}

export interface FluxoCaixaLancamento {
  id: number;
  tenant_id: number;
  lancamento_tipo: FluxoCaixaTipo;
  categoria: FluxoCaixaCategoria;
  centro_custo?: FluxoCaixaCentroCusto | null;
  valor: number;
  descricao?: string | null;
  documento?: string | null;
  data_lancamento: string;
  lancado_por: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface FluxoCaixaFormData {
  lancamento_tipo: FluxoCaixaTipo;
  categoria: FluxoCaixaCategoria;
  centro_custo?: FluxoCaixaCentroCusto | null;
  valor: number;
  descricao?: string;
  documento?: string;
  data_lancamento: string;
}

export interface FluxoCaixaPeriodo {
  dataInicio: string;
  dataFim: string;
}

export interface FluxoCaixaFiltros extends FluxoCaixaPeriodo {
  tipo?: FluxoCaixaTipo | null;
  categorias?: FluxoCaixaCategoria[];
  centrosCusto?: FluxoCaixaCentroCusto[];
}

export interface FluxoCaixaResumo {
  total_entrada: number;
  total_saida: number;
  saldo: number;
}

export interface FluxoCaixaCategoriaLinha {
  categoria: FluxoCaixaCategoria;
  entrada: number;
  saida: number;
  liquido: number;
  percentual: number;
}

export interface FluxoCaixaEvolucaoMensal {
  mes: string;
  label: string;
  entrada: number;
  saida: number;
  saldo: number;
}

export type FluxoCaixaExportFormato = 'xlsx' | 'csv';

export const FLUXO_CAIXA_TIPO_LABELS: Record<FluxoCaixaTipo, string> = {
  [FluxoCaixaTipo.ENTRADA]: 'Entrada',
  [FluxoCaixaTipo.SAIDA]: 'Saída',
};

export const FLUXO_CAIXA_CATEGORIA_LABELS: Record<FluxoCaixaCategoria, string> = {
  [FluxoCaixaCategoria.VENDA]: 'Venda',
  [FluxoCaixaCategoria.COMISSAO]: 'Comissão Recebida',
  [FluxoCaixaCategoria.DEVOLVIDA]: 'Devolvida',
  [FluxoCaixaCategoria.MULTA_CLIENTE]: 'Multa de Cliente',
  [FluxoCaixaCategoria.JUROS]: 'Juros',
  [FluxoCaixaCategoria.OUTRO_ENTRADA]: 'Outro (Entrada)',
  [FluxoCaixaCategoria.SALARIO]: 'Salário',
  [FluxoCaixaCategoria.COMISSAO_PAGA]: 'Comissão Paga',
  [FluxoCaixaCategoria.ALUGUEL]: 'Aluguel',
  [FluxoCaixaCategoria.ENERGIA]: 'Energia',
  [FluxoCaixaCategoria.AGUA]: 'Água',
  [FluxoCaixaCategoria.INTERNET]: 'Internet',
  [FluxoCaixaCategoria.TELEFONE]: 'Telefone',
  [FluxoCaixaCategoria.MANUTENCAO]: 'Manutenção',
  [FluxoCaixaCategoria.IMPOSTO]: 'Imposto',
  [FluxoCaixaCategoria.FORNECEDOR]: 'Fornecedor',
  [FluxoCaixaCategoria.TRANSPORTE]: 'Transporte',
  [FluxoCaixaCategoria.MARKETING]: 'Marketing',
  [FluxoCaixaCategoria.OUTRO_SAIDA]: 'Outro (Saída)',
};

export const FLUXO_CAIXA_CENTRO_CUSTO_LABELS: Record<FluxoCaixaCentroCusto, string> = {
  [FluxoCaixaCentroCusto.OPERACIONAL]: 'Operacional',
  [FluxoCaixaCentroCusto.COMERCIAL]: 'Comercial',
  [FluxoCaixaCentroCusto.ADMINISTRATIVO]: 'Administrativo',
  [FluxoCaixaCentroCusto.FINANCEIRO]: 'Financeiro',
  [FluxoCaixaCentroCusto.RECURSOS_HUMANOS]: 'Recursos Humanos',
  [FluxoCaixaCentroCusto.TECNOLOGIA]: 'Tecnologia',
};

export const CATEGORIAS_ENTRADA: FluxoCaixaCategoria[] = [
  FluxoCaixaCategoria.VENDA,
  FluxoCaixaCategoria.COMISSAO,
  FluxoCaixaCategoria.DEVOLVIDA,
  FluxoCaixaCategoria.MULTA_CLIENTE,
  FluxoCaixaCategoria.JUROS,
  FluxoCaixaCategoria.OUTRO_ENTRADA,
];

export const CATEGORIAS_SAIDA: FluxoCaixaCategoria[] = [
  FluxoCaixaCategoria.SALARIO,
  FluxoCaixaCategoria.COMISSAO_PAGA,
  FluxoCaixaCategoria.ALUGUEL,
  FluxoCaixaCategoria.ENERGIA,
  FluxoCaixaCategoria.AGUA,
  FluxoCaixaCategoria.INTERNET,
  FluxoCaixaCategoria.TELEFONE,
  FluxoCaixaCategoria.MANUTENCAO,
  FluxoCaixaCategoria.IMPOSTO,
  FluxoCaixaCategoria.FORNECEDOR,
  FluxoCaixaCategoria.TRANSPORTE,
  FluxoCaixaCategoria.MARKETING,
  FluxoCaixaCategoria.OUTRO_SAIDA,
];

export function getCategoriasPorTipo(tipo: FluxoCaixaTipo): FluxoCaixaCategoria[] {
  return tipo === FluxoCaixaTipo.ENTRADA ? CATEGORIAS_ENTRADA : CATEGORIAS_SAIDA;
}

export function getDefaultPeriodoResumo(): FluxoCaixaPeriodo {
  const hoje = dayjs();
  return {
    dataInicio: hoje.startOf('month').format('YYYY-MM-DD'),
    dataFim: hoje.format('YYYY-MM-DD'),
  };
}

export function getDefaultFiltrosTabela(): FluxoCaixaFiltros {
  const hoje = dayjs();
  return {
    dataInicio: hoje.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    dataFim: hoje.format('YYYY-MM-DD'),
    tipo: null,
    categorias: [],
    centrosCusto: [],
  };
}
